/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 水印提取进度弹窗组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-12 10:33:02
 */

import React, { useState, useEffect, useRef } from 'react'
import { Modal } from 'antd'
import './WatermarkExtractionModal.scss'
import { SafetyCertificateOutlined } from '@ant-design/icons'

interface WatermarkExtractionModalProps {
  visible: boolean
  photoCount: number
}

const WatermarkExtractionModal: React.FC<WatermarkExtractionModalProps> = ({
  visible,
  photoCount
}) => {
  const [progress, setProgress] = useState(0)
  const progressIntervalRef = useRef<any>(null)
  const startTimeRef = useRef<number | null>(null)
  const completionAnimationRef = useRef<any>(null)
  const currentProgressRef = useRef<number>(0)

  // 处理进度自动增长逻辑（先快后慢）
  useEffect(() => {
    if (visible) {
      // 记录开始时间
      startTimeRef.current = Date.now()
      setProgress(1) // 从1%开始
      currentProgressRef.current = 1

      // 清除之前的定时器和完成动画
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }
      if (completionAnimationRef.current) {
        clearInterval(completionAnimationRef.current)
      }

      // 创建进度更新定时器（每200ms更新一次）
      // 使用缓动函数，让进度条先快后慢增长
      progressIntervalRef.current = setInterval(() => {
        if (startTimeRef.current === null) {
          startTimeRef.current = Date.now()
        }

        const elapsed = Date.now() - startTimeRef.current
        // 使用固定的总时长（比如15秒）来计算进度比例
        // 这样无论照片数量多少，进度条都会在15秒内达到95%
        const totalDuration = 15 * 1000 // 15秒
        const progressRatio = Math.min(elapsed / totalDuration, 0.95)

        // 使用缓动函数 easeOutCubic，让进度条先快后慢
        // 公式：1 - (1 - t)^3，让进度条前期增长快，后期增长慢
        const easedProgress = 1 - Math.pow(1 - progressRatio, 3)

        // 将进度映射到 1% - 95% 范围
        const newProgress = Math.min(1 + easedProgress * 94, 95)
        setProgress(newProgress)
        currentProgressRef.current = newProgress
      }, 200)
    } else {
      // 弹窗关闭时，快速将进度条从当前值增长到100%
      const startProgress = currentProgressRef.current

      // 清除之前的定时器
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }

      if (startProgress < 100) {
        // 快速完成动画：在300ms内从当前进度增长到100%
        const targetProgress = 100
        const duration = 1000 // 300ms完成
        const animationStartTime = Date.now()

        completionAnimationRef.current = setInterval(() => {
          const elapsed = Date.now() - animationStartTime
          const ratio = Math.min(elapsed / duration, 1)

          // 使用缓动函数，让完成动画更平滑
          const easedRatio = 1 - Math.pow(1 - ratio, 2)
          const newProgress =
            startProgress + (targetProgress - startProgress) * easedRatio

          setProgress(newProgress)
          currentProgressRef.current = newProgress

          if (ratio >= 1) {
            // 动画完成，清除定时器
            if (completionAnimationRef.current) {
              clearInterval(completionAnimationRef.current)
              completionAnimationRef.current = null
            }
            // 延迟一点再重置进度，让用户看到100%
            setTimeout(() => {
              setProgress(0)
              currentProgressRef.current = 0
            }, 200)
          }
        }, 16) // 约60fps
      } else {
        // 如果已经是100%，直接重置
        if (completionAnimationRef.current) {
          clearInterval(completionAnimationRef.current)
          completionAnimationRef.current = null
        }
        setProgress(0)
        currentProgressRef.current = 0
        startTimeRef.current = null
      }
    }

    // 清理函数
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
      if (completionAnimationRef.current) {
        clearInterval(completionAnimationRef.current)
        completionAnimationRef.current = null
      }
      startTimeRef.current = null
    }
  }, [visible])

  return (
    <Modal
      open={visible}
      closable={false}
      mask={{ closable: false }}
      width={600}
      title={`${photoCount}张照片提取中，请耐心等待...`}
      footer={null}
      destroyOnHidden={false}
      zIndex={10000}
    >
      <div className="modalBody">
        {/* 进度条 */}
        <div className="progressContainer">
          <div className="progressBar">
            <div className="progressFill" style={{ width: `${progress}%` }} />
          </div>
          <div className="progressText">{Math.floor(progress)}%</div>
        </div>

        {/* 提示内容 */}
        <div className="tips">
          <div className="tipItem info">仅支持「今日水印相机」拍摄的照片</div>
          <div className="tipItem warning">
            <SafetyCertificateOutlined className="tipIcon" />
            水印时间和地点不可修改
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default WatermarkExtractionModal
